import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PORTFOLIO, CATEGORY_LABEL } from "@/lib/portfolioData";

const getThumb = (id, isShort) =>
  isShort
    ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
    : `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

/**
 * PortfolioCategoryStrip
 * ----------------------
 * A compact 4-tile cross-link block that lives on Service pages and /for/*
 * landing pages. Shows the top items from ONE or MORE portfolio categories
 * and deep-links each into the filtered /portfolio?category=<slug> view.
 *
 * Props:
 *   categories  — string | string[]   slug(s) to source items from
 *   heading     — string              section title (e.g. "See the work")
 *   subheading  — string              optional supporting copy
 *   limit       — number              max tiles to show (default 4)
 *   ctaLabel    — string              override the "See all X" CTA text
 */
export const PortfolioCategoryStrip = ({
  categories,
  heading = "See the work",
  subheading,
  limit = 4,
  ctaLabel,
}) => {
  const slugs = Array.isArray(categories) ? categories : [categories];
  const primary = slugs[0];

  // Pull items in editorial order from PORTFOLIO, matching any of the
  // requested categories (primary or extraCategories). De-dupe by id.
  const seen = new Set();
  const items = [];
  for (const item of PORTFOLIO) {
    const matches =
      slugs.includes(item.category) ||
      (Array.isArray(item.extraCategories) &&
        item.extraCategories.some((c) => slugs.includes(c)));
    if (!matches) continue;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    items.push(item);
    if (items.length >= limit) break;
  }

  if (items.length === 0) return null;

  const targetHref =
    slugs.length === 1 ? `/portfolio?category=${primary}` : "/portfolio";
  const primaryLabel = CATEGORY_LABEL[primary] || "portfolio";
  const finalCtaLabel = ctaLabel || `See all ${primaryLabel.toLowerCase()} work`;

  return (
    <section
      data-testid={`portfolio-strip-${primary}`}
      className="section-pad bg-[hsl(225,22%,4%)]"
    >
      <div className="container-site">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8 sm:mb-12">
          <div>
            <p className="kicker mb-3">Selected work</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(1.75rem,4.5vw,3rem)] leading-[0.95]">
              {heading}
            </h2>
            {subheading && (
              <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
                {subheading}
              </p>
            )}
          </div>
          <Link
            to={targetHref}
            data-testid={`portfolio-strip-cta-${primary}`}
            className="inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            {finalCtaLabel} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/portfolio?category=${item.category}`}
              data-testid={`portfolio-strip-tile-${item.id}`}
              className="group relative overflow-hidden rounded-xl border border-border bg-card text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            >
              <AspectRatio ratio={item.isShort ? 9 / 16 : 16 / 9}>
                <img
                  src={getThumb(item.youtubeId, item.isShort)}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = `https://i.ytimg.com/vi/${item.youtubeId}/hqdefault.jpg`;
                  }}
                />
              </AspectRatio>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              {item.isShort && (
                <div className="absolute top-3 right-3 inline-flex items-center rounded-full bg-black/60 backdrop-blur-sm border border-white/20 px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] text-white">
                  Reel
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                  <Play className="h-4 w-4 fill-current" />
                </span>
              </div>
              <div className="absolute inset-x-0 bottom-0 px-4 py-3">
                <div className="text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                  {CATEGORY_LABEL[item.category]}
                </div>
                <div className="mt-1 font-display text-sm sm:text-base uppercase text-white tracking-[0.02em] leading-tight line-clamp-2">
                  {item.title}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioCategoryStrip;
